import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, CalendarDays, MessageSquare, LogOut, Download, Loader2, Inbox, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MarketingAI } from "@/components/sections/MarketingAI";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    nouveau: "bg-accent/20 text-accent", en_attente: "bg-amber-500/20 text-amber-300",
    traite: "bg-green-500/20 text-green-300", confirme: "bg-green-500/20 text-green-300",
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full ${map[status] || "bg-white/10 text-muted-foreground"}`}>{status}</span>;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const a = t.admin;
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/admin/stats"), api.get("/admin/quotes"), api.get("/admin/appointments"), api.get("/admin/contacts"),
    ]).then(([s, q, ap, c]) => {
      setStats(s.data); setQuotes(q.data); setAppointments(ap.data); setContacts(c.data);
    }).catch((err) => {
      console.error("Admin dashboard load error:", err);
      toast.error("Erreur de chargement des données.");
    }).finally(() => setLoading(false));
  }, [user]);

  const downloadFile = async (id) => {
    try {
      const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) {
      console.error("File download error:", e);
      toast.error("Impossible de télécharger le document.");
    }
  };

  const updateQuoteStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    setQuotes((qs) => qs.map((q) => q.id === id ? { ...q, status } : q));
  };
  const updateAptStatus = async (id, status) => {
    await api.patch(`/admin/appointments/${id}`, { status });
    setAppointments((as) => as.map((x) => x.id === id ? { ...x, status } : x));
  };

  if (user === null || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-accent animate-spin" /></div>;
  }

  const cards = [
    { icon: FileText, label: a.quotes, value: stats?.quotes, sub: `${stats?.new_quotes} ${a.newq}` },
    { icon: CalendarDays, label: a.appointments, value: stats?.appointments },
    { icon: MessageSquare, label: a.contacts, value: stats?.contacts },
  ];

  return (
    <div className="min-h-screen">
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo /><span className="hidden sm:inline text-sm text-muted-foreground">· {a.space}</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/admin/login"); }} data-testid="logout-btn" className="rounded-full border-white/10">
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {a.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {cards.map(({ icon: Icon, label, value, sub }, i) => (
            <div key={i} className="glow-border glass rounded-2xl p-6" data-testid={`stat-card-${i}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="font-display text-3xl font-bold mt-1">{value ?? 0}</p>
                  {sub && <p className="text-xs text-accent mt-1">{sub}</p>}
                </div>
                <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center"><Icon className="w-5 h-5 text-accent" strokeWidth={1.5} /></div>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="quotes">
          <TabsList className="bg-white/5 border border-white/10 flex flex-wrap h-auto gap-1 w-full sm:w-auto justify-start">
            <TabsTrigger value="quotes" data-testid="tab-quotes">{a.quotes}</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">{a.appointments}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">{a.contacts}</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing" className="data-[state=active]:bg-accent data-[state=active]:text-white">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> IA Marketing
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-5 space-y-3" data-testid="quotes-list">
            {quotes.length === 0 && <Empty />}
            {quotes.map((q) => (
              <div key={q.id} className="rounded-xl border border-white/10 bg-card p-5" data-testid={`quote-row-${q.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-medium">{q.name} · <span className="text-accent">{q.formula}</span> · {q.sector}</p>
                    <p className="text-sm text-muted-foreground mt-1">{q.email} · {q.phone} {q.company && `· ${q.company}`}</p>
                    {q.vehicle && <p className="text-sm mt-1">{q.vehicle}</p>}
                    {q.message && <p className="text-sm text-muted-foreground mt-2">{q.message}</p>}
                    {q.file_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {q.file_ids.map((fid, idx) => (
                          <button key={fid} onClick={() => downloadFile(fid)} data-testid={`download-file-${fid}`}
                            className="flex items-center gap-1.5 text-xs rounded-full border border-white/10 px-3 py-1.5 hover:border-accent hover:text-accent transition-colors">
                            <Download className="w-3.5 h-3.5" /> Doc {idx + 1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <StatusBadge status={q.status} />
                    <select value={q.status} onChange={(e) => updateQuoteStatus(q.id, e.target.value)} data-testid={`quote-status-${q.id}`}
                      className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1 outline-none">
                      <option value="nouveau" className="bg-[#0d2138]">nouveau</option>
                      <option value="en_attente" className="bg-[#0d2138]">en_attente</option>
                      <option value="traite" className="bg-[#0d2138]">traité</option>
                    </select>
                    <span className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="appointments" className="mt-5 space-y-3" data-testid="appointments-list">
            {appointments.length === 0 && <Empty />}
            {appointments.map((ap) => (
              <div key={ap.id} className="rounded-xl border border-white/10 bg-card p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`apt-row-${ap.id}`}>
                <div>
                  <p className="font-medium flex items-center gap-2"><CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.5} /> {ap.date} · {ap.time}</p>
                  <p className="text-sm text-muted-foreground mt-1">{ap.name} · {ap.email} · {ap.phone}</p>
                  {ap.sector && <p className="text-sm mt-1">{ap.sector}</p>}
                  {ap.message && <p className="text-sm text-muted-foreground mt-1">{ap.message}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={ap.status} />
                  <select value={ap.status} onChange={(e) => updateAptStatus(ap.id, e.target.value)} data-testid={`apt-status-${ap.id}`}
                    className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1 outline-none">
                    <option value="en_attente" className="bg-[#0d2138]">en_attente</option>
                    <option value="confirme" className="bg-[#0d2138]">confirmé</option>
                  </select>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="contacts" className="mt-5 space-y-3" data-testid="contacts-list">
            {contacts.length === 0 && <Empty />}
            {contacts.map((c) => (
              <div key={c.id} className="rounded-xl border border-white/10 bg-card p-5" data-testid={`contact-row-${c.id}`}>
                <p className="font-medium">{c.name} · <span className="text-muted-foreground">{c.email}</span></p>
                <p className="text-sm text-muted-foreground mt-2">{c.message}</p>
                <p className="text-xs text-muted-foreground/60 mt-2">{new Date(c.created_at).toLocaleString()}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="marketing" className="mt-5" data-testid="marketing-tab">
            <MarketingAI embedded />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Empty = () => (
  <div className="rounded-xl border border-dashed border-white/10 p-12 text-center text-muted-foreground">
    <Inbox className="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} /> Aucune donnée
  </div>
);
